import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import "./NewSales.scss";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useCurrency } from "../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import {
	isHospitalConnected as isPharmacyHospitalConnected,
	pharmacyCollectsPayment as getPharmacyCollectsPayment,
} from "../../utils/pharmacyConnection";
import { usePosBarcodeInput } from "../../Hooks/usePosBarcodeInput";
import {
	pickBestProductMatch,
	searchProducts,
} from "../../utils/barcodeUtils";
import {
	HIGHLIGHT_ROW_MS,
	playPosScanTone,
	posScanLog,
} from "../../utils/posScannerUtils";

import CustomerSelector from "./NewSales/components/CustomerSelector";
import CartTable from "./NewSales/components/CartTable";
import CartSummary from "./NewSales/components/CartSummary";
import ProductGrid from "./NewSales/components/ProductGrid";
import PrescriptionSummary from "./NewSales/components/PrescriptionSummary";
import PrescriptionOffcanvas from "./NewSales/components/PrescriptionOffcanvas";
import PaymentModal from "./NewSales/components/PaymentModal";
import BillingPaymentModal from "./NewSales/components/BillingPaymentModal";
import TreatmentPackageBillingPaymentModal from "./NewSales/components/TreatmentPackageBillingPaymentModal";
import BillingInvoiceModal from "./NewSales/components/BillingInvoiceModal";
import BackendInvoicePreviewModal from "./NewSales/components/BackendInvoicePreviewModal";
import InvoiceModal from "./NewSales/components/InvoiceModal";
import BatchWarningModal from "./NewSales/components/BatchWarningModal";
import NewCustomerModal from "./NewSales/components/NewCustomerModal";

import { useSalesCart } from "./NewSales/hooks/useSalesCart";
import { usePrescriptionManager } from "./NewSales/hooks/usePrescriptionManager";
import { usePaymentManager } from "./NewSales/hooks/usePaymentManager";
import { useBillingManager } from "./NewSales/hooks/useBillingManager";
import { useMedicineResearchSearch } from "./NewSales/hooks/useMedicineResearchSearch";
import { mapPatientsToOptions, buildPatientOptionLabel } from "./NewSales/utils/patientUtils";
import { getSectionDiscountFromSummary, resolveActivePrescription, getPriorPharmacyItemsFromPrescription } from "./NewSales/utils/billingUtils";
import { toMoneyNumber } from "../../utils/financeEngine";
import {
	getPatientAccountInvoiceId,
	savePatientAccountRecord,
} from "../PatientAccounts/utils/patientAccountUtils";
import { isPlaceholderDoctorName } from "./NewSales/utils/doctorFormUtils";
import { calculateItemTax } from "./NewSales/utils/taxCalculations";
import {
	getActivePosTaxType,
	getCartItemForTaxCalculation,
	shouldShowPosTaxUi,
} from "../PharmacyManageTax/taxConfigUtils";

import { itemsMaster } from "../PharmacyStocks/StockService";
import { createPharmacyCustomer, getAllPatients, getPatients, getTopSoldItems } from "./SalesService";
import { getTaxesAPI } from "../PharmacyManageTax/TaxService";
import { useLicense } from "../../_helper/license/LicenseContext";

const LAST_CUSTOMER_TYPE_KEY = "last_customer_type";


const NewSales = () => {
	const location = useLocation();
	const prescriptionData = location.state?.prescriptionData || null;
	const { patientId, patientName, stateCustomerType } = location.state || {};

	const { t } = useTranslation();
	const { loading, setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const { getCurrencySymbol, convertCurrency, currency, allowedCurrencies, refreshRates } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();

	const isHospitalConnected = isPharmacyHospitalConnected(pharmacyPOS);
	const pharmacyCollectsPayment = getPharmacyCollectsPayment(pharmacyPOS);
	// const isHospitalConnected = true;
	// const isHospitalConnected = pharmacyPOS?.isHospitalConnected || false;
	const hospitalData = isHospitalConnected ? pharmacyPOS?.hospital : null;
	const { decodeTokenOnly } = useLicense();
	const [canUploadPrescription, setCanUploadPrescription] = useState(false);

	// Tax system states
	const [pharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
	const [patientStateCode, setPatientStateCode] = useState("");
	const posTaxType = useMemo(
		() => getActivePosTaxType(pharmacyPOS, "gst"),
		[pharmacyPOS]
	);
	const [itemTaxes, setItemTaxes] = useState({});

	const patientSelectRef = useRef(null);
	const searchInputRef = useRef(null);
	const searchDebounceRef = useRef(null);

	const [contentHeight, setContentHeight] = useState(window.innerHeight - 166);
	const [searchQuery, setSearchQuery] = useState("");
	const [highlightProductId, setHighlightProductId] = useState(null);
	const [products, setProducts] = useState([]);
	const [isOTC, setIsOTC] = useState(false);
	const {
		medicines: researchMedicines,
		loading: researchMedicinesLoading,
		isActive: isMedicineResearchActive,
	} = useMedicineResearchSearch(searchQuery, isOTC);
	const [discountType, setDiscountType] = useState("Percentage");
	const [discountValue, setDiscountValue] = useState(0);
	const [transportCost, setTransportCost] = useState(0);
	const [taxes, setTaxes] = useState([]);

	const [customerType, setCustomerType] = useState(() => {
		const stored = localStorage.getItem(LAST_CUSTOMER_TYPE_KEY);
		return stored || "walking";
	});

	const [walkingCustomerName, setWalkingCustomerName] = useState("");
	const [selectedPatient, setSelectedPatient] = useState(null);
	const [customerError, setCustomerError] = useState(false);
	const [hasNewCustomer, setHasNewCustomer] = useState(false);
	const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
	const [newCustomerData, setNewCustomerData] = useState({
		name: "",
		mobileNumber: "",
		phoneCode: "",
		country: "",
		state: "",
		stateCode: ""
	});
	const [newCustomerErrors, setNewCustomerErrors] = useState({});
	const [createTooltipOpen, setCreateTooltipOpen] = useState(false);
	const [selectedPatientOption, setSelectedPatientOption] = useState(null);

	const [prescriptionFile, setPrescriptionFile] = useState(null);
	const lastDoctorHydrationRef = useRef(null);

	useEffect(() => {
		decodeTokenOnly().then((decoded) => {
			if (decoded?.features) {
				setCanUploadPrescription(
					decoded.features.prescription_upload === true
				);
			}
		});
	}, [decodeTokenOnly]);

	const {
		cart,
		batchWarning,
		resetBatchWarning,
		addProductToCart,
		upsertCartItem,
		handleBatchChange,
		updateItemField,
		handleTaxChange,
		handleQuantityChange,
		removeItem,
		addNewBatchEntry,
		resetCart,
	} = useSalesCart(t);

	const showPosTax = useMemo(
		() => shouldShowPosTaxUi(pharmacyPOS, cart),
		[pharmacyPOS, cart]
	);

	// Calculate item taxes whenever cart or state codes change
	useEffect(() => {
		if (cart.length > 0) {
			const newItemTaxes = {};
			cart.forEach(item => {
				const taxInfo = calculateItemTax(
					getCartItemForTaxCalculation(pharmacyPOS, item),
					pharmacyStateCode,
					patientStateCode,
					posTaxType
				);
				newItemTaxes[`${item._id}-${item.selectedBatchId}`] = taxInfo;
			});
			setItemTaxes(newItemTaxes);
		} else {
			setItemTaxes({});
		}
	}, [cart, pharmacyStateCode, patientStateCode, posTaxType, pharmacyPOS]);

	const {
		paymentData,
		setPaymentData,
		paidType,
		setPaidType,
		showPaymentModal,
		openPaymentModal,
		closePaymentModal,
		handlePaymentSave,
		errors,
		showInvoice,
		invoiceData,
		closeInvoice,
		resetPaymentState,
		totals,
		handleSettlePayment,
		prescriptionDate,
		setPrescriptionDate,
		doctorForm,
		updateDoctorForm,
		clearDoctorSelection,
		doctorId,
		setDoctorId,
		selectedDoctorOption,
		setSelectedDoctorOption,
		handleAddDoctorToMaster,
		addingDoctor,
		doctorFormVersion,
		hydrateDoctorFromPrescription,
	} = usePaymentManager({
		t,
		cart,
		itemTaxes,
		discountType,
		discountValue,
		transportCost,
		customerType,
		selectedPatient,
		prescriptionData,
		pharmacyPOS,
		getCurrencySymbol,
		setLoading,
		convertCurrency,
		currency,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		prescriptionFile
	});

	const loadMedicines = useCallback(
		async (inputValue, isOTC) => {
			if (!inputValue) {
				setProducts([]);
				return [];
			}

			const body = {
				search: inputValue,
				clinicId: pharmacyPOS?.hospital_id,
			};

			try {
				const response = await itemsMaster(body);
				const medicines = response.data?.data || [];
				setProducts(medicines);
				return medicines.map((medicine) => ({
					value: medicine._id,
					label: `${medicine.brandName} [${medicine.saltName}]`,
					raw: medicine,
				}));
			} catch (error) {
				console.error("Error fetching medicines:", error);
				setProducts([]);
				return [];
			}
		},
		[pharmacyPOS?.hospital_id]
	);

	const syncMedicineSearch = useCallback((searchTerm) => {
		const trimmed = searchTerm?.trim() || "";
		if (!trimmed || trimmed.length < 2) return;
		setSearchQuery(trimmed);
	}, []);

	const {
		recentPrescriptions,
		showPrescriptionsCard,
		setShowPrescriptionsCard,
		showPrescriptionOffcanvas,
		setShowPrescriptionOffcanvas,
		selectedPrescription,
		setSelectedPrescription,
		consultationOpen,
		toggleConsultation,
		setConsultationOpen,
		addedPrescriptions,
		setAddedPrescriptions,
		cartPrescriptionId,
		clearCartPrescription,
		addPrescriptionToCart,
		loadRecentPrescriptions,
		handleSelectPrescription,
		setRecentPrescriptions,
		prescriptionDetails,
		setPrescriptionDetails,
	} = usePrescriptionManager({
		t,
		loadMedicines,
		upsertCartItem,
		resetCart,
		getCartLength: () => cart.length,
		setLoading,
		isHospitalConnected,
		customerType,
		pharmacyPOS,
		onMedicineSearch: syncMedicineSearch,
	});

	const buildSalePaymentData = useCallback(
		({ currency, type, amount, paidType }) => ({
			...paymentData,
			currency,
			type,
			amount: String(amount),
			patientName:
				paymentData.patientName ||
				(selectedPatient
					? buildPatientOptionLabel(selectedPatient, isHospitalConnected)
					: ""),
			partialPaid: paidType === "partial",
			isSettle: paidType === "full",
		}),
		[isHospitalConnected, paymentData, selectedPatient]
	);

	const {
		showBillingModal,
		billingLoading,
		billingSubmitting,
		showBillingInvoice,
		billingInvoiceData,
		closeBillingInvoice,
		showBackendInvoice,
		backendInvoiceUrl,
		backendInvoiceNo,
		closeBackendInvoice,
		openBillingModal,
		closeBillingModal,
		resetBillingState,
		activePrescription,
		serviceItems,
		servicesBillingTotals,
		servicesDiscountType,
		servicesDiscountValue,
		servicesTaxConfig,
		investigationsBillingTotals,
		investigationsDiscountType,
		investigationsDiscountValue,
		investigationsTaxConfig,
		investigationItems,
		suggestedInvestigations,
		generalMasterList,
		labMasterList,
		priorPharmacyItems,
		billingSummary,
		billingBaseCurrency,
		defaultCurrency: billingDefaultCurrency,
		payment: billingPayment,
		updatePayment: updateBillingPayment,
		errors: billingErrors,
		submitted: billingSubmitted,
		sectionOpen,
		toggleSection,
		updateInvestigationAmount,
		updateInvestigationQuantity,
		updateServiceAmount,
		updateServiceQuantity,
		updateServicesDiscount,
		updateServicesTaxConfig,
		updateInvestigationsDiscount,
		updateInvestigationsTaxConfig,
		removeInvestigation,
		removeServiceItem,
		addServiceRow,
		addInvestigationRow,
		selectServiceForRow,
		selectInvestigationForRow,
		searchGeneralServices,
		searchLabTests,
		paymentBalance,
		handleBillingSubmit,
		isTreatmentPackage,
		treatmentDetails,
		treatmentPaymentHistory,
		packagePrice,
	} = useBillingManager({
		t,
		pharmacyPOS,
		prescriptionDetails,
		selectedPrescription,
		recentPrescriptions,
		cartPrescriptionId,
		selectedPatient,
		cart,
		totals,
		itemTaxes,
		discountType,
		discountValue,
		transportCost,
		customerType,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		convertCurrency,
		refreshRates,
		currency,
		allowedCurrencies,
		buildSalePaymentData,
		setLoading,
		prescriptionFile,
		getCurrencySymbol,
		doctorId,
		doctorForm,
		setDoctorId,
		hydrateDoctorFromPrescription,
		pharmacyCollectsPayment,
	});

	const loadTopSoldItems = useCallback(async () => {
		try {
			const payload = {
				clinicId: pharmacyPOS?.hospital_id
			}
			setLoading(true);
			const response = await getTopSoldItems(payload);
			const medicines = response.data?.data || [];
			setProducts(medicines);
		} catch (error) {
			console.error("Error fetching top sold items:", error);
		} finally {
			setLoading(false);
		}
	}, [pharmacyPOS?.hospital_id, setLoading]);

	const fetchTaxes = useCallback(async () => {
		try {
			setLoading(true);
			const payload = {
				clinicId: pharmacyPOS?.hospital_id
			}
			const response = await getTaxesAPI(payload);
			setTaxes(response.data?.data?.taxes || []);
		} catch (error) {
			console.error("Error fetching taxes:", error);
		} finally {
			setLoading(false);
		}
	}, [pharmacyPOS?.hospital_id, setLoading]);

	const loadPatients = useCallback(
		async (inputValue) => {
			if (!inputValue || inputValue.length < 3) return [];
			// console.log("Loading patients with customerType:", customerType);

			try {
				const payload = {
					search: inputValue,
				}

				// Only add clinic_id if hospital connected AND customer type is hospital_patient
				if (isHospitalConnected && customerType === "hospital_patient") {
					payload.clinic_id = pharmacyPOS?.hospital_id;
					// console.log("Added clinic_id:", pharmacyPOS?.hospital_id);
				}

				const response = await getAllPatients(payload);
				const patients = response.data?.data || [];

				return mapPatientsToOptions(patients, isHospitalConnected, customerType);
			} catch (error) {
				console.error("Error searching patients:", error);
				return [];
			}
		},
		[isHospitalConnected, customerType, pharmacyPOS?.hospital_id] // ✅ Added customerType and hospital_id
	);

	const updatePaymentData = useCallback(
		(patch) => {
			setPaymentData((prev) => ({ ...prev, ...patch }));
		},
		[setPaymentData]
	);

	const selectExistingCustomer = useCallback(
		(patient) => {
			const option = {
				value: patient._id,
				label: buildPatientOptionLabel(patient, isHospitalConnected),
				data: patient,
			};

			setSelectedPatient(patient);
			setSelectedPatientOption(option);
			setPatientStateCode(patient.stateCode || "");
			updatePaymentData({ patientName: option.label });

			// Ensure AsyncSelect is mounted
			// requestAnimationFrame(() => {
			// 	if (patientSelectRef.current?.setValue) {
			// 		patientSelectRef.current.setValue(option);
			// 	}
			// });
		},
		[isHospitalConnected, updatePaymentData]
	);

	const handleCustomerTypeChange = useCallback(
		(type) => {
			resetSale();
			setCustomerType(type);
			setCustomerError(false);
			setAddedPrescriptions(new Set());
			setSelectedPatientOption(null);

			if (type === "walking") {
				setSelectedPatient(null);
				setPatientStateCode("");
				updatePaymentData({ patientName: walkingCustomerName });
				setShowPrescriptionsCard(false);
				setRecentPrescriptions([]);
				setSelectedPrescription(null);

				if (patientSelectRef.current) {
					patientSelectRef.current.setValue(null);
				}
			}
			// console.log(selectedPatient);

			if ((type === "inpatient" || type === "hospital_patient") && selectedPatient) {
				selectExistingCustomer(selectedPatient);
			}

			// ✅ Log the parameter directly, not the state variable
			// console.log("Customer type changed to:", type);
			localStorage.setItem("LAST_CUSTOMER_TYPE_KEY", type);
		},
		[
			walkingCustomerName,
			selectedPatient,
			selectExistingCustomer,
			setAddedPrescriptions,
			setRecentPrescriptions,
			setSelectedPrescription,
			setShowPrescriptionsCard,
			updatePaymentData,
		]
	);

	const handleCreateNewCustomer = useCallback(async () => {
		const validationErrors = {};
		if (!newCustomerData.name.trim()) {
			validationErrors.name = t("Name is required");
		}
		if (!newCustomerData.mobileNumber.trim()) {
			validationErrors.mobileNumber = t("Mobile number is required");
		}

		setNewCustomerErrors(validationErrors);
		if (Object.keys(validationErrors).length) return;

		try {
			setLoading(true);
			const payload = {
				patient_name: newCustomerData.name,
				phone: newCustomerData.mobileNumber,
				countryCode: newCustomerData.phoneCode,
				country: newCustomerData.country,
				state: newCustomerData.state,
				stateCode: newCustomerData.stateCode,
			};
			const response = await createPharmacyCustomer(payload);
			if (response.data?.status === "success") {
				toast.success(t(response.data?.message || "Customer created successfully"));
				const respData = response.data?.data || {};
				const createdCustomer = {
					_id: respData._id || `temp_${Date.now()}`,
					full_name: respData.patient_name || newCustomerData.name,
					patient_name: respData.patient_name || newCustomerData.name,
					mobile_number: respData.phone || newCustomerData.mobileNumber,
					phone: respData.phone || newCustomerData.mobileNumber,
					countryCode: respData.countryCode || newCustomerData.phoneCode || "",
					stateCode: respData.stateCode || newCustomerData.stateCode || "",
					...respData,
				};

				// Switch type FIRST
				setCustomerType("inpatient");

				// Mark as new customer
				setHasNewCustomer(true);

				// Select customer AFTER switch using selectExistingCustomer
				selectExistingCustomer(createdCustomer);
				// Cleanup
				setShowNewCustomerModal(false);
				setNewCustomerData({
					name: "",
					mobileNumber: "",
					phoneCode: "",
					country: "",
					state: "",
					stateCode: "",
					city: "",
				});
				setNewCustomerErrors({});

			} else {
				console.log(response.data?.message);

				// toast.warning(t(response.data?.message || "Failed to create customer. Try again."));
			}
		} catch (error) {
			console.error("Error creating new customer:", error);
			// toast.warning(
			// 	t(
			// 		error.response?.data?.message ||
			// 		"Something went wrong while creating the customer"
			// 	)
			// );
		} finally {
			setLoading(false);
		}
	}, [
		isHospitalConnected,
		newCustomerData,
		setLoading,
		t,
		selectExistingCustomer, // ✅ Added to dependencies
		updatePaymentData,
	]);

	const handlePatientChange = useCallback(
		(selectedOption) => {
			setSelectedPatientOption(selectedOption);

			const rawPatient = selectedOption ? selectedOption.data : null;
			const patient = rawPatient
				? {
					...rawPatient,
					full_name: rawPatient.full_name || rawPatient.patient_name || rawPatient.name || "",
					patient_name: rawPatient.patient_name || rawPatient.full_name || rawPatient.name || "",
					name: rawPatient.name || rawPatient.full_name || rawPatient.patient_name || "",
					stateCode: rawPatient.stateCode || ""
				}
				: null;

			setSelectedPatient(patient);
			setPatientStateCode(patient?.stateCode || "");
			setCustomerError(false);
			resetCart();
			clearCartPrescription();
			setAddedPrescriptions(new Set());
			setSelectedPrescription(null);
			if (patient) {
				const name = buildPatientOptionLabel(patient, isHospitalConnected);
				updatePaymentData({ patientName: name });
				loadRecentPrescriptions(patient._id);
				setShowPrescriptionsCard(true);
			} else {
				updatePaymentData({ patientName: "" });
				setRecentPrescriptions([]);
				setShowPrescriptionsCard(false);
				setSelectedPrescription(null);
			}
		},
		[
			clearCartPrescription,
			isHospitalConnected,
			loadRecentPrescriptions,
			resetCart,
			setAddedPrescriptions,
			setRecentPrescriptions,
			setSelectedPrescription,
			setShowPrescriptionsCard,
			updatePaymentData,
		]
	);

	const updateNewCustomerData = useCallback((patch) => {
		setNewCustomerData((prev) => ({ ...prev, ...patch }));
	}, []);

	const handleWalkingNameChange = useCallback(
		(value) => {
			setWalkingCustomerName(value);
			updatePaymentData({ patientName: value });
			setCustomerError(false);
		},
		[updatePaymentData]
	);

	const handlePaidTypeChange = useCallback(
		(type) => {
			setPaidType(type);
			if (type === "full") {
				const netTotal = convertCurrency(totals.netTotal, currency, paymentData.currency);
				updatePaymentData({ amount: netTotal, partialPaid: false });
			} else {
				updatePaymentData({ partialPaid: true });
			}
		},
		[setPaidType, totals.netTotal, updatePaymentData, currency, paymentData.currency]
	);

	const handleOpenPaymentModal = useCallback(() => {
		setCustomerError(false);
		const netAmount = totals.netTotal;
		if (cart.length) {
			if (customerType === "inpatient" || customerType === "hospital_patient") {
				if (!selectedPatient) {
					setCustomerError(true);
					Swal.fire({
						icon: "info",
						text: t("Please select a patient first"),
						confirmButtonText: t("OK"),
					});
					return;
				}

				const name = buildPatientOptionLabel(selectedPatient, isHospitalConnected);

				if (customerType === "hospital_patient" && pharmacyCollectsPayment) {
					const cartPrescription = cartPrescriptionId
						? recentPrescriptions?.find((item) => item._id === cartPrescriptionId)
						: null;
					const prescription = resolveActivePrescription(
						prescriptionDetails,
						cartPrescription,
						selectedPrescription
					);
					// Restore pharmacy discount/transport from previously saved billing
					const savedSummary = prescription?.billing?.summary || {};
					const pharmacyDiscount = getSectionDiscountFromSummary(
						savedSummary,
						"pharmacy"
					);
					if (pharmacyDiscount.discountValue > 0 || savedSummary.pharmacy_discount_type) {
						setDiscountType(pharmacyDiscount.discountType);
						setDiscountValue(pharmacyDiscount.discountValue);
					}
					const savedTransport = toMoneyNumber(savedSummary.transport ?? 0);
					if (savedTransport > 0) {
						setTransportCost(savedTransport);
					}
					openBillingModal({ prescription });
					return;
				}

				openPaymentModal({ customerName: name, amount: netAmount });
			} else {
				if (paymentData.patientName && paymentData.patientName.trim() !== "") {
					openPaymentModal({ customerName: paymentData.patientName, amount: netAmount });
				} else {
					// setCustomerError(true);
					setWalkingCustomerName("Walk-in Customer");
					openPaymentModal({ customerName: 'Walk-in Customer', amount: netAmount });
					// Swal.fire({
					// 	icon: "info",
					// 	text: t("Please enter walk-in customer name"),
					// 	confirmButtonText: t("OK"),
					// });
				}
			}
		} else {
			setCustomerError(true);
			Swal.fire({
				icon: "info",
				text: t("Please select product(s)."),
				confirmButtonText: t("OK"),
			});
		}
	}, [
		cart.length,
		cartPrescriptionId,
		customerType,
		isHospitalConnected,
		openBillingModal,
		openPaymentModal,
		paymentData.patientName,
		pharmacyCollectsPayment,
		prescriptionDetails,
		recentPrescriptions,
		selectedPatient,
		selectedPrescription,
		t,
		totals.netTotal,
	]);

	const runProductSearch = useCallback(
		async (value) => {
			if (!value) {
				await loadTopSoldItems();
				return;
			}
			if (value.length < 2) {
				setProducts([]);
				return;
			}
			await loadMedicines(value, isOTC);
		},
		[loadMedicines, loadTopSoldItems, isOTC]
	);

	const handleSearchChange = useCallback(
		(value) => {
			setSearchQuery(value);
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
			}
			if (!value) {
				runProductSearch("");
				return;
			}
			if (value.length < 2) {
				setProducts([]);
				return;
			}
			searchDebounceRef.current = setTimeout(() => {
				runProductSearch(value);
			}, 300);
		},
		[runProductSearch]
	);

	const handleBarcodeScan = useCallback(
		async (barcode) => {
			posScanLog("handleBarcodeScan", barcode);
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
			}

			const trimmed = String(barcode).trim();

			try {
				const medicines = await searchProducts(
					trimmed,
					pharmacyPOS?.hospital_id
				);
				const product = pickBestProductMatch(medicines, trimmed);
				posScanLog("lookup result", { count: medicines.length, productId: product?._id });

				if (!product) {
					playPosScanTone("error");
					toast.error(t("Product not found"));
					setSearchQuery("");
					setProducts([]);
					return;
				}

				if (product.totalRemainingMedicine === 0) {
					playPosScanTone("error");
					toast.warning(t("Out of stock"));
					setSearchQuery(trimmed);
					setProducts(medicines);
					return;
				}

				const alreadyInCart = cart.some((item) => item._id === product._id);
				addProductToCart(product);
				playPosScanTone("success");
				setHighlightProductId(product._id);
				window.setTimeout(() => setHighlightProductId(null), HIGHLIGHT_ROW_MS);

				// toast.success(
				// 	alreadyInCart
				// 		? t("Quantity updated: {{name}}", {
				// 				name: product.brandName || product.saltName,
				// 			})
				// 		: t("Product added: {{name}}", {
				// 				name: product.brandName || product.saltName,
				// 			})
				// );
				// add beep sound
				setSearchQuery("");
				setProducts([]);
				await loadTopSoldItems();
			} catch (error) {
				console.error("Barcode scan error:", error);
				playPosScanTone("error");
				toast.error(t("Product not found"));
				setSearchQuery("");
				setProducts([]);
			}
		},
		[
			addProductToCart,
			cart,
			loadTopSoldItems,
			pharmacyPOS?.hospital_id,
			t,
		]
	);

	const { getSearchInputProps, refocusSearch } = usePosBarcodeInput({
		onScan: handleBarcodeScan,
		onValueChange: handleSearchChange,
		inputRef: searchInputRef,
		enabled: true,
	});

	const searchInputProps = getSearchInputProps();

	useEffect(() => {
		refocusSearch();
	}, [refocusSearch]);

	const printInvoice = useCallback(() => {
		const invoiceContent = document?.getElementById("invoice-content");
		if (!invoiceContent) return;
		const printWindow = window.open("", "", "width=400,height=600");
		if (!printWindow) return;
		printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            @page { size: 80mm auto; margin: 5mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .bold { font-weight: bold; }
            .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            td { padding: 2px 0; vertical-align: top; }
            .section { margin-top: 6px; margin-bottom: 6px; }
            .thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>${invoiceContent.innerHTML}</body>
      </html>
    `);
		printWindow.document.close();
		printWindow.focus();
		setTimeout(() => {
			printWindow.print();
			printWindow.close();
		}, 400);
	}, []);

	const resetSale = useCallback(() => {
		resetCart();
		resetBatchWarning();
		resetPaymentState();
		resetBillingState();
		setProducts([]);
		setSearchQuery("");
		setDiscountType("Percentage");
		setDiscountValue(0);
		setTransportCost(0);
		// console.log(customerType);
		setCustomerType(customerType || "walking");
		setWalkingCustomerName("");
		setSelectedPatient(null);
		setSelectedPatientOption(null);
		setPatientStateCode("");
		setCustomerError(false);
		setHasNewCustomer(false);
		setRecentPrescriptions([]);
		setShowPrescriptionsCard(false);
		// close the prescription offcanvas
		setShowPrescriptionOffcanvas(false);
		setSelectedPrescription(null);
		setConsultationOpen(false);
		setPrescriptionDetails(null);
		setAddedPrescriptions(new Set());
		clearCartPrescription();
		setItemTaxes({});
		setNewCustomerData({ name: "", mobileNumber: "", phoneCode: "", country: "", state: "", stateCode: "", city: "" });
		setNewCustomerErrors({});
		// if (patientSelectRef.current) {
		// 	patientSelectRef.current.setValue(null);
		// }
		updatePaymentData({
			amount: "",
			patientName: "",
			partialPaid: null,
			isSettle: false,
		});
		loadTopSoldItems();
		setPrescriptionFile(null);
		lastDoctorHydrationRef.current = null;
		// reset the location state to prevent reloading prescription on reset
		location.state = {};
	}, [
		clearCartPrescription,
		customerType,
		loadTopSoldItems,
		resetBatchWarning,
		resetBillingState,
		resetCart,
		resetPaymentState,
		setAddedPrescriptions,
		setConsultationOpen,
		setRecentPrescriptions,
		setSelectedPrescription,
		setShowPrescriptionsCard,
		setShowPrescriptionOffcanvas,
		setPrescriptionDetails,
		updatePaymentData,
	]);

	const handleSubmitPayment = useCallback(async () => {
		const invoice = await handlePaymentSave();
		return invoice;
	}, [handlePaymentSave]);

	useEffect(() => {
		const activePrescription = prescriptionDetails || prescriptionData;
		const prescriptionKey = activePrescription?._id || activePrescription?.id;
		const prescriptionDoctor = activePrescription?.doctor;

		if (
			!prescriptionKey ||
			!prescriptionDoctor?.name?.trim() ||
			isPlaceholderDoctorName(prescriptionDoctor.name)
		) {
			return;
		}
		if (lastDoctorHydrationRef.current === prescriptionKey) return;

		lastDoctorHydrationRef.current = prescriptionKey;
		hydrateDoctorFromPrescription(prescriptionDoctor);
	}, [prescriptionDetails, prescriptionData, hydrateDoctorFromPrescription]);

	useEffect(() => {
		if (!prescriptionData) return;

		const pId =
			prescriptionData.patientId ||
			prescriptionData.patient_id ||
			prescriptionData.patient?._id ||
			prescriptionData.patient?.id ||
			prescriptionData.appointment_invoice?.patient_id;
		const pName =
			prescriptionData.patientName ||
			prescriptionData.patient?.name ||
			prescriptionData.patient?.full_name ||
			prescriptionData.patient?.first_name;

		if (!pId) {
			Swal.fire({
				icon: "warning",
				title: "Incomplete Prescription",
				text: "Patient details are missing in the prescription.",
			});
			return;
		}

		setCustomerType("hospital_patient");

		const patient = {
			_id: pId,
			name: pName,
			stateCode: prescriptionData.patient?.stateCode || "",
			...prescriptionData.patient,
		};

		patient.full_name = pName;
		patient.patient_name = pName;
		patient._id = pId;

		setSelectedPatient(patient);
		setPatientStateCode(patient.stateCode || "");
		updatePaymentData({ patientName: patient.name || pName });

		setSelectedPatientOption({
			value: patient._id,
			label: patient.name || pName,
			data: patient,
		});

		resetCart();

		// Re-dispense: keep cart for NEW medicines only. Prior pharmacy lines come from billing.
		const hasPriorPharmacy =
			getPriorPharmacyItemsFromPrescription(prescriptionData).length > 0;
		const prescriptionLines = Array.isArray(prescriptionData.prescriptions)
			? prescriptionData.prescriptions
			: [];
		if (!hasPriorPharmacy) {
			prescriptionLines.forEach((item) => {
				const cartItem = normalizePrescriptionItemToCartItem(item);
				upsertCartItem(cartItem);
			});
		}

		const billingSummary = prescriptionData?.billing?.summary || {};
		const pharmacyDiscount = getSectionDiscountFromSummary(billingSummary, "pharmacy");
		if (
			pharmacyDiscount.discountValue > 0 ||
			billingSummary.pharmacy_discount_type ||
			prescriptionData.discountType
		) {
			setDiscountType(
				pharmacyDiscount.discountValue > 0 || billingSummary.pharmacy_discount_type
					? pharmacyDiscount.discountType
					: prescriptionData.discountType || "Percentage"
			);
		}
		if (pharmacyDiscount.discountValue > 0) {
			setDiscountValue(pharmacyDiscount.discountValue);
		} else if (prescriptionData.discount) {
			setDiscountValue(Number(prescriptionData.discount));
		}
		const savedTransport = toMoneyNumber(
			billingSummary.transport ?? prescriptionData.transportCost ?? 0
		);
		if (savedTransport > 0) {
			setTransportCost(savedTransport);
		}

		// Keep full invoice/billing context for prior pharmacy append on re-dispense.
		setRecentPrescriptions([prescriptionData]);
		setSelectedPrescription(prescriptionData);
		setPrescriptionDetails(prescriptionData);
		setShowPrescriptionsCard(true);
		setShowPrescriptionOffcanvas(true);
	}, [
		prescriptionData,
		resetCart,
		upsertCartItem,
		updatePaymentData,
		setPrescriptionDetails,
		setRecentPrescriptions,
		setSelectedPrescription,
		setShowPrescriptionsCard,
		setShowPrescriptionOffcanvas,
	]);

	useEffect(() => {
		const measureViewport = () => {
			const header = document.querySelector(".page-header");
			const headerHeight = header?.offsetHeight ?? 80;
			setContentHeight(window.innerHeight - headerHeight - 8);
		};
		window.addEventListener("resize", measureViewport);
		measureViewport();
		return () => window.removeEventListener("resize", measureViewport);
	}, []);

	const { cartScrollHeight, summaryScrollHeight } = useMemo(() => {
		const actionBar = 58;
		const chrome = 96;
		const customer = 50;
		const summaryDiscount = 42;
		const available = Math.max(200, contentHeight - actionBar - chrome - customer - summaryDiscount);
		const hasTaxSummary = showPosTax && totals.tax > 0 && cart.length > 0;

		if (hasTaxSummary) {
			const cartH = Math.max(100, Math.floor(available * 0.54));
			const summaryH = Math.max(120, available - cartH);
			return { cartScrollHeight: cartH, summaryScrollHeight: summaryH };
		}

		const summaryH = cart.length > 0
			? Math.max(110, Math.floor(available * 0.32))
			: 72;
		const cartH = Math.max(120, available - summaryH);
		return { cartScrollHeight: cartH, summaryScrollHeight: summaryH };
	}, [contentHeight, totals.tax, cart.length, showPosTax]);

	useEffect(() => {
		loadTopSoldItems();
		fetchTaxes();
	}, [fetchTaxes, loadTopSoldItems]);

	useEffect(() => {
		const initData = async () => {
			if (prescriptionData) {
				const resolvedPatientId =
					prescriptionData.patientId ||
					prescriptionData.patient_id ||
					prescriptionData.patient?._id ||
					prescriptionData.patient?.id ||
					prescriptionData.appointment_invoice?.patient_id;

				if (!resolvedPatientId) {
					console.warn(
						"Prescription data missing patient id. Skipping secondary prescription preload."
					);
					return;
				}

				const patientNameResolved =
					prescriptionData.patientName ||
					prescriptionData.patient?.name ||
					prescriptionData.patient?.full_name ||
					"";

				const patient = {
					...(prescriptionData.patient || {}),
					_id: resolvedPatientId,
					full_name: patientNameResolved,
					patient_name: patientNameResolved,
					name: patientNameResolved,
					stateCode: prescriptionData.patient?.stateCode || "",
				};
				setSelectedPatient(patient);
				setPatientStateCode(patient.stateCode || "");
				setCustomerType(prescriptionData.customerType || "hospital_patient");
				setRecentPrescriptions([prescriptionData]);
				setSelectedPrescription(prescriptionData);
				setPrescriptionDetails(prescriptionData);
				setShowPrescriptionOffcanvas(true);
				setShowPrescriptionsCard(true);
				setConsultationOpen(false);
				updatePaymentData({ patientName: patientNameResolved });

				setSelectedPatientOption({
					value: patient._id,
					label: patient.name,
					data: patient,
				});
			} else if (patientId && patientName) {
				setLoading(true);
				const patients = await loadPatients(patientName);
				const patient = patients.find((p) => p.value === patientId);
				handlePatientChange(patient);
				setCustomerType(stateCustomerType);
				updatePaymentData({ patientName });
				setLoading(false);
			}
		};

		initData();
	}, [
		handlePatientChange,
		loadPatients,
		patientId,
		patientName,
		prescriptionData,
		setConsultationOpen,
		setPrescriptionDetails,
		setRecentPrescriptions,
		setSelectedPrescription,
		setShowPrescriptionOffcanvas,
		setShowPrescriptionsCard,
		setLoading,
		stateCustomerType,
		updatePaymentData,
	]);

	useEffect(() => {
		if (customerType) {
			localStorage.setItem(LAST_CUSTOMER_TYPE_KEY, customerType);
		}
	}, [customerType]);

	const normalizePrescriptionItemToCartItem = (item) => {
		return {
			_id: item.itemId,
			stockId: item.stockId,
			medicineName: item.medicineName,
			brandName: item.brandName,
			productForm: item.productForm,
			price: Number(item.unitPricePerMedicine),
			quantity: Number(item.medicineQunatity || 1),
			selectedBatchId: item.batchNumber,
			batchNumber: item.batchNumber,
			MRP: Number(item.MRP),
			discount: Number(item.discount || 0),
			isOTC: item.isOTC,
			isIGST: item.isIGST,
			taxPercentage: Number(item.taxPercentage || 0),
		};
	};

	return (
		<Fragment>
			<div className="new-sales new-sales-theme pos-viewport" style={{ height: `${contentHeight}px` }}>
				<div className="content-wrapper h-100">
					<Row className="pt-1 g-3 pos-content h-100 mx-0">
						<Col sm={12} md={8} className="h-100">
							<div className="pos-left-column">
								<Card className="pos-cart-card">
									<Card.Body className="p-2">
										<CustomerSelector
											t={t}
											isHospitalConnected={isHospitalConnected}
											hospitalData={hospitalData}
											customerType={customerType}
											onCustomerTypeChange={handleCustomerTypeChange}
											walkingCustomerName={walkingCustomerName}
											onWalkingNameChange={handleWalkingNameChange}
											customerError={customerError}
											patientSelectRef={patientSelectRef}
											loadPatients={loadPatients}
											onPatientChange={handlePatientChange}
											hasNewCustomer={hasNewCustomer}
											onCreateCustomerClick={() => {
												setShowNewCustomerModal(true);
												updatePaymentData({ patientName: "" });
											}}
											createTooltipOpen={createTooltipOpen}
											toggleCreateTooltip={() => setCreateTooltipOpen((prev) => !prev)}
											disableScrollProps={disableScrollProps}
											selectedPatientOption={selectedPatientOption}
										/>
										<Scrollbars
											autoHide={false}
											hideTracksWhenNotNeeded
											style={{ height: `${cartScrollHeight}px` }}
											universal
										// renderTrackHorizontal={(props) => <div {...props} style={{ display: "none" }} />}
										>
											<CartTable
												posCompact={true}
												showPosTax={showPosTax}
												t={t}
												cart={cart}
												highlightProductId={highlightProductId}
												totals={totals}
												getCurrencySymbol={getCurrencySymbol}
												onBatchChange={handleBatchChange}
												onPriceChange={updateItemField}
												onQuantityChange={handleQuantityChange}
												onTaxChange={handleTaxChange}
												onRemoveItem={removeItem}
												disableScrollProps={disableScrollProps}
												itemTaxes={itemTaxes}
												posTaxType={posTaxType}
												pharmacyStateCode={pharmacyStateCode}
												patientStateCode={patientStateCode}
												discountType={discountType}
												discountValue={discountValue}
											/>
										</Scrollbars>
									</Card.Body>
								</Card>
								<Card className="pos-summary-card mb-0">
									<Card.Body className="pos-summary-card__body p-2">
										<CartSummary
											showPosTax={showPosTax}
											scrollHeight={summaryScrollHeight}
											t={t}
											discountType={discountType}
											onDiscountTypeChange={setDiscountType}
											discountValue={discountValue}
											onDiscountValueChange={(value) => setDiscountValue(Number(value) || 0)}
											transportCost={transportCost}
											onTransportCostChange={setTransportCost}
											taxes={taxes}
											totals={totals}
											getCurrencySymbol={getCurrencySymbol}
											disableScrollProps={disableScrollProps}
											posTaxType={posTaxType}
											pharmacyStateCode={pharmacyStateCode}
											patientStateCode={patientStateCode}
											cart={cart}
											itemTaxes={itemTaxes}
										/>
									</Card.Body>
								</Card>

								<div className="pos-action-footer">
									<div className="d-flex w-100 justify-content-end gap-2 py-2 px-4 border-top mb-0">
										<Button
											variant="primary"
											size="md"
											onClick={handleOpenPaymentModal}
										>
											{t("Proceed to Pay")}
										</Button>
										<Button variant="secondary" size="md" onClick={resetSale}>
											{t("Reset")}
										</Button>
									</div>
								</div>
							</div>
						</Col>
						<Col sm={12} md={4} className="h-100 pos-right-column">
							<div className="pos-right-column__inner" style={{ height: `${contentHeight}px` }}>
								{showPrescriptionsCard && recentPrescriptions.length > 0 && (
									<PrescriptionSummary
										t={t}
										selectedPatient={selectedPatient}
										recentPrescriptions={recentPrescriptions}
										onClose={() => setShowPrescriptionsCard(false)}
										onSelectPrescription={handleSelectPrescription}
										isHospitalConnected={isHospitalConnected}
										customerType={customerType}
										addedPrescriptions={addedPrescriptions}
									/>
								)}
								<ProductGrid
									t={t}
									searchRef={searchInputRef}
									searchQuery={searchQuery}
									searchInputProps={searchInputProps}
									products={products}
									onProductClick={addProductToCart}
									researchMedicines={researchMedicines}
									researchMedicinesLoading={researchMedicinesLoading}
									isMedicineResearchActive={isMedicineResearchActive}
									isOTC={isOTC}
									setIsOTC={setIsOTC}
								/>
							</div>
						</Col>
					</Row>
				</div>
				<PrescriptionOffcanvas
					t={t}
					show={showPrescriptionOffcanvas}
					onHide={() => {
						setShowPrescriptionOffcanvas(false);
						setSelectedPrescription(null);
					}}
					selectedPrescription={selectedPrescription}
					consultationOpen={consultationOpen}
					onToggleConsultation={toggleConsultation}
					onAddAllToCart={() => selectedPrescription && addPrescriptionToCart(selectedPrescription)}
					loading={loading}
					addedPrescriptions={addedPrescriptions}
					isHospitalConnected={isHospitalConnected}
					customerType={customerType}
					prescriptionDetails={prescriptionDetails}
				/>
				{isTreatmentPackage ? (
					<TreatmentPackageBillingPaymentModal
						show={showBillingModal}
						isLoading={billingLoading}
						onHide={() => {
							closeBillingModal();
							refocusSearch();
						}}
						activePrescription={activePrescription}
						selectedPatient={selectedPatient}
						treatmentDetails={treatmentDetails}
						treatmentPaymentHistory={treatmentPaymentHistory}
						packagePrice={packagePrice}
						serviceItems={serviceItems}
						servicesBillingTotals={servicesBillingTotals}
						servicesDiscountType={servicesDiscountType}
						servicesDiscountValue={servicesDiscountValue}
						servicesTaxConfig={servicesTaxConfig}
						onServicesDiscountChange={updateServicesDiscount}
						onServicesTaxChange={updateServicesTaxConfig}
						investigationsBillingTotals={investigationsBillingTotals}
						investigationsDiscountType={investigationsDiscountType}
						investigationsDiscountValue={investigationsDiscountValue}
						investigationsTaxConfig={investigationsTaxConfig}
						onInvestigationsDiscountChange={updateInvestigationsDiscount}
						onInvestigationsTaxChange={updateInvestigationsTaxConfig}
						investigationItems={investigationItems}
						suggestedInvestigations={suggestedInvestigations}
						generalMasterList={generalMasterList}
						labMasterList={labMasterList}
						searchGeneralServices={searchGeneralServices}
						searchLabTests={searchLabTests}
						cart={cart}
						priorPharmacyItems={priorPharmacyItems}
						itemTaxes={itemTaxes}
						totals={totals}
						discountType={discountType}
						discountValue={discountValue}
						posTaxType={posTaxType}
						pharmacyStateCode={pharmacyStateCode}
						patientStateCode={patientStateCode}
						showPosTax={showPosTax}
						billingSummary={billingSummary}
						billingBaseCurrency={billingBaseCurrency}
						defaultCurrency={billingDefaultCurrency}
						convertCurrency={convertCurrency}
						payment={billingPayment}
						onPaymentChange={updateBillingPayment}
						errors={billingErrors}
						submitted={billingSubmitted}
						sectionOpen={sectionOpen}
						onToggleSection={toggleSection}
						updateInvestigationAmount={updateInvestigationAmount}
						updateInvestigationQuantity={updateInvestigationQuantity}
						updateServiceAmount={updateServiceAmount}
						updateServiceQuantity={updateServiceQuantity}
						onRemoveInvestigation={removeInvestigation}
						onRemoveServiceItem={removeServiceItem}
						onAddServiceRow={addServiceRow}
						onSelectServiceForRow={selectServiceForRow}
						onAddInvestigationRow={addInvestigationRow}
						onSelectInvestigationForRow={selectInvestigationForRow}
						paymentBalance={paymentBalance}
						onCollectPayment={() => handleBillingSubmit({ markAsPaid: false })}
						onCollectAndMarkPaid={() => handleBillingSubmit({ markAsPaid: true })}
						isSubmitting={billingSubmitting || loading}
						doctorId={doctorId}
						doctorForm={doctorForm}
						onDoctorFormChange={updateDoctorForm}
						onDoctorIdChange={setDoctorId}
						selectedDoctorOption={selectedDoctorOption}
						onSelectedDoctorOptionChange={setSelectedDoctorOption}
						onClearDoctorSelection={clearDoctorSelection}
						onAddDoctorToMaster={handleAddDoctorToMaster}
						addingDoctor={addingDoctor}
					/>
				) : (
				<BillingPaymentModal
					show={showBillingModal}
					isLoading={billingLoading}
					onHide={() => {
						closeBillingModal();
						refocusSearch();
					}}
					activePrescription={activePrescription}
					selectedPatient={selectedPatient}
					serviceItems={serviceItems}
					servicesBillingTotals={servicesBillingTotals}
					servicesDiscountType={servicesDiscountType}
					servicesDiscountValue={servicesDiscountValue}
					servicesTaxConfig={servicesTaxConfig}
					onServicesDiscountChange={updateServicesDiscount}
					onServicesTaxChange={updateServicesTaxConfig}
					investigationsBillingTotals={investigationsBillingTotals}
					investigationsDiscountType={investigationsDiscountType}
					investigationsDiscountValue={investigationsDiscountValue}
					investigationsTaxConfig={investigationsTaxConfig}
					onInvestigationsDiscountChange={updateInvestigationsDiscount}
					onInvestigationsTaxChange={updateInvestigationsTaxConfig}
					investigationItems={investigationItems}
					suggestedInvestigations={suggestedInvestigations}
					generalMasterList={generalMasterList}
					labMasterList={labMasterList}
					searchGeneralServices={searchGeneralServices}
					searchLabTests={searchLabTests}
					cart={cart}
					priorPharmacyItems={priorPharmacyItems}
					itemTaxes={itemTaxes}
					totals={totals}
					discountType={discountType}
					discountValue={discountValue}
					posTaxType={posTaxType}
					pharmacyStateCode={pharmacyStateCode}
					patientStateCode={patientStateCode}
					showPosTax={showPosTax}
					billingSummary={billingSummary}
					billingBaseCurrency={billingBaseCurrency}
					defaultCurrency={billingDefaultCurrency}
					convertCurrency={convertCurrency}
					payment={billingPayment}
					onPaymentChange={updateBillingPayment}
					errors={billingErrors}
					submitted={billingSubmitted}
					sectionOpen={sectionOpen}
					onToggleSection={toggleSection}
					updateInvestigationAmount={updateInvestigationAmount}
					updateInvestigationQuantity={updateInvestigationQuantity}
					updateServiceAmount={updateServiceAmount}
					updateServiceQuantity={updateServiceQuantity}
					onRemoveInvestigation={removeInvestigation}
					onRemoveServiceItem={removeServiceItem}
					onAddServiceRow={addServiceRow}
					onSelectServiceForRow={selectServiceForRow}
					onAddInvestigationRow={addInvestigationRow}
					onSelectInvestigationForRow={selectInvestigationForRow}
					paymentBalance={paymentBalance}
					onCollectPayment={() => handleBillingSubmit({ markAsPaid: false })}
					onCollectAndMarkPaid={() => handleBillingSubmit({ markAsPaid: true })}
					isSubmitting={billingSubmitting || loading}
					doctorId={doctorId}
					doctorForm={doctorForm}
					onDoctorFormChange={updateDoctorForm}
					onDoctorIdChange={setDoctorId}
					selectedDoctorOption={selectedDoctorOption}
					onSelectedDoctorOptionChange={setSelectedDoctorOption}
					onClearDoctorSelection={clearDoctorSelection}
					onAddDoctorToMaster={handleAddDoctorToMaster}
					addingDoctor={addingDoctor}
				/>
				)}
				<PaymentModal
					t={t}
					show={showPaymentModal}
					onHide={() => {
						closePaymentModal();
						refocusSearch();
					}}
					paymentData={paymentData}
					onPaymentDataChange={updatePaymentData}
					paidType={paidType}
					onPaidTypeChange={handlePaidTypeChange}
					customerType={customerType}
					errors={errors}
					handleSave={handleSubmitPayment}
					getCurrencySymbol={getCurrencySymbol}
					disableScrollProps={disableScrollProps}
					totals={totals}
					handleSettlePayment={handleSettlePayment}
					convertCurrency={convertCurrency}
					defaultCurrency={currency}
					canUploadPrescription={canUploadPrescription}
					prescriptionFile={prescriptionFile}
					onPrescriptionFileChange={setPrescriptionFile}
					prescriptionDate={prescriptionDate}
					onPrescriptionDateChange={setPrescriptionDate}
					doctorForm={doctorForm}
					onDoctorFormChange={updateDoctorForm}
					onClearDoctorSelection={clearDoctorSelection}
					doctorId={doctorId}
					onDoctorIdChange={setDoctorId}
					selectedDoctorOption={selectedDoctorOption}
					onSelectedDoctorOptionChange={setSelectedDoctorOption}
					onAddDoctorToMaster={handleAddDoctorToMaster}
					addingDoctor={addingDoctor}
					doctorFormVersion={doctorFormVersion}
				/>
				<BillingInvoiceModal
					t={t}
					show={showBillingInvoice}
					invoiceData={billingInvoiceData}
					onHide={() => {
						closeBillingInvoice();
						resetSale();
						refocusSearch();
					}}
					onClose={() => {
						closeBillingInvoice();
						resetSale();
						refocusSearch();
					}}
					onPrint={printInvoice}
					pharmacyData={pharmacyPOS}
				/>
				<BackendInvoicePreviewModal
					t={t}
					show={showBackendInvoice}
					invoiceUrl={backendInvoiceUrl}
					invoiceNo={backendInvoiceNo}
					onHide={() => {
						closeBackendInvoice();
						resetSale();
						refocusSearch();
					}}
					onClose={() => {
						closeBackendInvoice();
						resetSale();
						refocusSearch();
					}}
				/>
				<InvoiceModal
					t={t}
					show={showInvoice}
					invoiceData={invoiceData}
					onHide={() => {
						closeInvoice();
						resetSale();
						refocusSearch();
					}}
					onClose={() => {
						closeInvoice();
						resetSale();
						refocusSearch();
					}}
					onPrint={printInvoice}
					pharmacyData={pharmacyPOS}
				/>
				<BatchWarningModal
					t={t}
					show={batchWarning.show}
					onCancel={() => {
						resetBatchWarning();
						refocusSearch();
					}}
					onConfirm={() => {
						if (batchWarning.item) {
							addNewBatchEntry(batchWarning.item);
						}
						refocusSearch();
					}}
					batchWarning={batchWarning}
				/>
				<NewCustomerModal
					t={t}
					show={showNewCustomerModal}
					onHide={() => {
						setShowNewCustomerModal(false);
						setNewCustomerData({ name: "", mobileNumber: "", phoneCode: "", country: "", state: "", stateCode: "", city: "" });
						setNewCustomerErrors({});
					}}
					newCustomerData={newCustomerData}
					onChange={updateNewCustomerData}
					errors={newCustomerErrors}
					onSubmit={handleCreateNewCustomer}
				/>
			</div>

		</Fragment>
	);
};

export default NewSales;